import { useState } from 'react';
import { apiClient as api } from '../../lib/api';
import { toast } from '../../lib/toast';
import { MoveAmSupervisorModal } from './MoveAmSupervisorModal';

/**
 * The admin hierarchy moves that UserAccessModal exposes as buttons: moving an
 * AM under a new supervisor, and reassigning a clinic to a new AM. The modal
 * only raises the intent (onMoveSupervisor / onSelectNewAM); this hook owns
 * the preflight, the confirmation step and the API calls, so every page that
 * mounts the modal gets working buttons by spreading `modalProps` and
 * rendering `modals`.
 *
 * `onMoved` runs after a successful move so the host page can refetch its list.
 */
export function useHierarchyMoves(subject: any | null, onMoved: () => void) {
  const [refreshToken, setRefreshToken] = useState(0);
  const [showMoveSupervisor, setShowMoveSupervisor] = useState(false);
  const [newAM, setNewAM] = useState<any | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [missingProducts, setMissingProducts] = useState<Array<{ _id: string; name: string }>>([]);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setNewAM(null);
    setError(null);
    setReason('');
    setMissingProducts([]);
  };

  const onSelectNewAM = (am: any) => {
    reset();
    setNewAM(am);
    if (!subject) return;
    // Fire-and-forget preflight so the user sees product-block warnings before
    // they type a reason.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      setPreflightLoading(true);
      try {
        const res = await api.adminMovePreflight(subject._id, am._id);
        if (res.success && res.data && !res.data.canMove) {
          // Prefer named products; fall back to ids so the warning still surfaces.
          const named = res.data.missingProducts
            ?? res.data.missingProductIds.map((id: string) => ({ _id: id, name: id }));
          setMissingProducts(named);
          setError(`New AM is missing ${named.length} product(s) the physician currently sells.`);
        }
      } catch {
        // non-blocking
      } finally {
        setPreflightLoading(false);
      }
    })();
  };

  const confirmReassignment = async () => {
    if (!subject || !newAM) return;
    const trimmed = reason.trim();
    if (trimmed.length < 5 || trimmed.length > 500) {
      setError('Please enter a reason (5–500 chars).');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setMissingProducts([]);
      const response = await api.adminReassignPhysician(subject._id, newAM._id, trimmed);
      if (response.success) {
        toast.success('Physician moved. Catalog assignments preserved.');
        reset();
        // The modal owns its own data; nudge it to refetch in place.
        setRefreshToken(n => n + 1);
        onMoved();
      } else if (response.code === 'products_unavailable_under_new_parent') {
        const named = response.missingProducts
          ?? (response.missingProductIds || []).map(id => ({ _id: id, name: id }));
        setMissingProducts(named);
        setError(`${response.error}. Add the missing products to the new AM's catalog before retrying.`);
      } else {
        setError(response.error || 'Failed to move physician');
      }
    } catch (err) {
      console.error('Error moving physician:', err);
      setError('An unexpected error occurred during move');
    } finally {
      setLoading(false);
    }
  };

  const modalProps = {
    onMoveSupervisor: () => setShowMoveSupervisor(true),
    onSelectNewAM,
    refreshToken
  };

  const modals = (
    <>
      {showMoveSupervisor && subject && subject.role === 'distributor' && (
        <MoveAmSupervisorModal
          am={subject}
          onClose={() => setShowMoveSupervisor(false)}
          onSuccess={() => {
            setShowMoveSupervisor(false);
            setRefreshToken(n => n + 1);
            onMoved();
          }}
        />
      )}

      {newAM && subject && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div data-testid="reassign-confirmation-modal" className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Account Manager Reassignment</h3>
            </div>

            <div className="px-6 py-4">
              {error && (
                <div data-testid="reassign-error-state" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-sm text-red-800">{error}</div>
                </div>
              )}

              <div className="space-y-4">
                <div className="text-sm text-gray-700">
                  <p className="mb-2">
                    You are about to reassign <span className="font-medium">{subject.profile?.firstName} {subject.profile?.lastName}</span> to:
                  </p>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="font-medium text-blue-900">
                      {newAM.distributorData?.companyName || 'No Company'} - {newAM.profile?.firstName} {newAM.profile?.lastName}
                    </div>
                    <div className="text-sm text-blue-700">{newAM.email}</div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm text-blue-900">
                    A new catalog will be auto-created under the new AM with the physician's
                    current prices — they will not lose product access. The new AM can edit
                    those prices later.
                  </div>
                </div>

                {missingProducts.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <div className="font-medium mb-1">New AM is missing these products:</div>
                    <ul className="text-xs list-disc list-inside space-y-0.5">
                      {missingProducts.map(p => (
                        <li key={p._id}>{p.name}</li>
                      ))}
                    </ul>
                    <div className="mt-2">Add them to the new AM's catalog before retrying.</div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for move <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    rows={3}
                    minLength={5}
                    maxLength={500}
                    placeholder="e.g., territory realignment, AM departure, etc."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <div className="text-xs text-gray-500 mt-1">5–500 characters. Saved on the audit log.</div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={reset}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReassignment}
                disabled={loading || preflightLoading || missingProducts.length > 0 || reason.trim().length < 5}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Moving...
                  </>
                ) : preflightLoading ? (
                  'Checking products…'
                ) : (
                  'Confirm Move'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const cancel = () => {
    reset();
    setShowMoveSupervisor(false);
  };

  return { modalProps, modals, cancel };
}
